import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { FileUpload } from 'primeng/fileupload';

import { AppSettings } from '../../app.settings';
import { StorageService } from '../../services/storage.service';
import { LoaderService } from '../loader/loader.service';
import { CommonBindingDataService } from '../../services/common-binding-data.service';
import { UiService } from '../../services/ui.service';

const IMAGE_WILDCARD_EXTRAS = [
  'image/jpeg', '.jpg', '.jpeg',
  'image/png', '.png',
  'image/gif', '.gif',
  'image/bmp', '.bmp',
  'image/svg+xml', '.svg'
];

const BLOCKED_FILE_TYPES = new Set(['image/webp']);
const BLOCKED_FILE_EXTENSIONS = new Set(['.webp']);
const IMAGE_EXTENSIONS = new Set(IMAGE_WILDCARD_EXTRAS.filter(t => t.startsWith('.')));
const PDF_EXTRAS = ['application/pdf', '.pdf'];

@Component({
  selector: 'app-file-uploader-latest',
  templateUrl: './file.uploader-latest.component.html',
  styleUrl: './file.uploader-latest.component.scss',
})
export class FileUploaderLatestComponent implements OnInit {

  @Input() accept: string;
  @Input() maxSize: number = AppSettings.FILE_UPLOAD_MAX_SIZE_IN_BYTE;
  @Input() uploadButtonLabel: string;
  @Input() dropZoneLabel = 'Or drop Document here';
  @Input() maxFileCount = 0;
  @Input() currentFileCount = 0;
  @Input() imageMode = false;
  @Input() documentMode = false;
  @Input() previewImageUrl = '';
  @Input() defaultImageUrl = '';
  @Input() defaultDocumentIconUrl = '/assets/images/temp/doc.png';
  @Input() uploadedLabel = 'Image Uploaded';
  @Input() fileTypeHint = 'JPG, PNG, Maximum 10MB';
  @Input() dragDropTitle = '';
  @Input() selectButtonIcon = '';
  @Input() showRemoveButton = true;

  @Output() onFileUpload: EventEmitter<any> = new EventEmitter();
  @Output() onFileUploadEvent: EventEmitter<any> = new EventEmitter();
  @Output() onDroppedFile: EventEmitter<File> = new EventEmitter();
  @Output() onRemoveFile: EventEmitter<void> = new EventEmitter();

  @ViewChild('fileUpload') fileUpload: FileUpload;

  fileUploadUrl: string = AppSettings.UPLOAD_FILE_URL;
  maxSizeMessage: string;
  appheaders: HttpHeaders;
  isDragOver = false;
  eventIdParam: any;
  uploadedFileName = '';
  uploadedFileSize = '';
  private dragCounter = 0;

  get isCardMode(): boolean {
    return this.imageMode || this.documentMode;
  }

  get isImageUploaded(): boolean {
    if (this.documentMode) {
      return false;
    }
    const url = this.previewImageUrl;
    if (!url) {
      return false;
    }
    if (this.defaultImageUrl && url === this.defaultImageUrl) {
      return false;
    }
    return true;
  }

  get displayImageUrl(): string {
    if (this.documentMode) {
      return this.defaultDocumentIconUrl;
    }
    return this.previewImageUrl || this.defaultImageUrl;
  }

  get resolvedDragDropTitle(): string {
    if (this.dragDropTitle) {
      return this.dragDropTitle;
    }
    return this.documentMode
      ? 'Drag & Drop Your Document Here'
      : 'Drag & Drop Your Image Here';
  }

  get resolvedSelectButtonIcon(): string {
    if (this.selectButtonIcon) {
      return this.selectButtonIcon;
    }
    return this.documentMode ? 'pi pi-file' : 'pi pi-image';
  }

  get resolvedAccept(): string {
    if (!this.accept) {
      return '';
    }

    const types = new Set(
      this.accept.split(',').map(t => t.trim()).filter(Boolean)
    );
    const hasImageWildcard = [...types].some(t => t.toLowerCase() === 'image/*');

    if (hasImageWildcard) {
      types.delete('image/*');
      IMAGE_WILDCARD_EXTRAS.forEach(t => types.add(t));
    }

    const hasPdfType = [...types].some(t => t.toLowerCase() === 'application/pdf');
    if (hasPdfType) {
      PDF_EXTRAS.forEach(t => types.add(t));
    }

    return [...types].join(',');
  }

  constructor(
    private storageService: StorageService,
    private translate: TranslateService,
    private loaderService: LoaderService,
    private uiService: UiService,
    private commonService: CommonBindingDataService
  ) {
    this.appheaders = new HttpHeaders().set(
      'Authorization',
      'Bearer ' + this.storageService.getItemFromSessionStorage(AppSettings.TOKEN_KEY)
    );
  }

  ngOnInit() {
    this.commonService.eventId.subscribe(result => {
      this.eventIdParam = result;
      this.fileUploadUrl += '?eventId=' + this.eventIdParam;
    });

    if (!this.eventIdParam) {
      const tempEventId = this.commonService.changedEventId;
      if (tempEventId) {
        this.eventIdParam = tempEventId;
        this.fileUploadUrl += '?eventId=' + this.eventIdParam;
      }
    }

    this.maxSizeMessage = this.commonService.getLabel('file_size_limit');
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    this.isDragOver = true;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter--;

    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.isDragOver = false;
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return;
    }

    if (files.length > 1) {
      this.uiService.showMessage('Please drop only one file at a time', AppSettings.MESSAGE_TYPE.ERROR);
      return;
    }

    const file = files[0];

    if (file.size > this.maxSize) {
      this.uiService.showMessage(this.commonService.getLabel('file_upload_error'), AppSettings.MESSAGE_TYPE.ERROR);
      return;
    }

    if (this.accept && !this.isFileTypeAccepted(file)) {
      this.uiService.showMessage(this.commonService.getLabel('label_onlyjpg_jpeg_png_image_formats_are_supported'), AppSettings.MESSAGE_TYPE.ERROR);
      return;
    }

    if (this.isUploadLimitReached()) {
      return;
    }

    this.setUploadedFileMeta(file);
    this.onDroppedFile.emit(file);
    this.uploadDroppedFile(file, event);
  }

  private uploadDroppedFile(file: File, event: DragEvent): void {
    if (!this.fileUpload) {
      return;
    }

    this.fileUpload.clear();
    this.fileUpload.files = [file];
    this.onBeforeUpload({});

    setTimeout(() => {
      if (!this.fileUpload?.files?.length) {
        if (!this.isFileTypeAccepted(file)) {
          this.uiService.showMessage(this.commonService.getLabel('label_onlyjpg_jpeg_png_image_formats_are_supported'), AppSettings.MESSAGE_TYPE.ERROR);
          this.loaderService.hide();
          return;
        }
        this.fileUpload.files = [file];
      }

      if (!this.fileUpload?.files?.length) {
        this.loaderService.hide();
        return;
      }

      this.fileUpload.upload();
    });
  }

  onDropZoneClick(): void {
    this.fileUpload?.choose();
  }

  onChangeImageClick(): void {
    this.fileUpload?.choose();
  }

  onPreviewImageError(event: Event): void {
    const img = event?.target as HTMLImageElement;
    if (!img || !this.defaultImageUrl) {
      return;
    }
    if (img.src !== this.defaultImageUrl) {
      img.src = this.defaultImageUrl;
    }
  }

  onRemoveClick(): void {
    this.clearUploadedFileMeta();
    this.fileUpload?.clear();
    this.onRemoveFile.emit();
  }

  private setUploadedFileMeta(file: File): void {
    if (!file) {
      return;
    }
    this.uploadedFileName = file.name || '';
    const ext = (file.name.split('.').pop() || '').toUpperCase();
    const sizeLabel = this.formatFileSize(file.size);
    this.uploadedFileSize = ext && sizeLabel ? `${ext}, ${sizeLabel}` : (sizeLabel || ext);
  }

  private clearUploadedFileMeta(): void {
    this.uploadedFileName = '';
    this.uploadedFileSize = '';
  }

  formatFileSize(bytes: number): string {
    if (!bytes && bytes !== 0) {
      return '';
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private isFileTypeBlocked(file: File): boolean {
    const fileType = (file.type || '').toLowerCase();
    const fileExt = '.' + (file.name.split('.').pop() || '').toLowerCase();
    return BLOCKED_FILE_TYPES.has(fileType) || BLOCKED_FILE_EXTENSIONS.has(fileExt);
  }

  private isFileTypeAccepted(file: File): boolean {
    if (this.isFileTypeBlocked(file)) {
      return false;
    }

    const accept = this.resolvedAccept;
    if (!accept) {
      return true;
    }

    const acceptedTypes = accept.split(',').map(t => t.trim().toLowerCase());
    const fileType = (file.type || '').toLowerCase();
    const fileExt = '.' + (file.name.split('.').pop() || '').toLowerCase();

    return acceptedTypes.some(type => {
      if (type === '*' || type === '.*') {
        return true;
      }
      if (fileType && type === fileType) {
        return true;
      }
      if (type === fileExt) {
        return true;
      }
      if (type.endsWith('/*')) {
        const category = type.replace('/*', '');
        if (fileType && fileType.startsWith(category + '/')) {
          return true;
        }
        if (!fileType && category === 'image' && IMAGE_EXTENSIONS.has(fileExt)) {
          return true;
        }
      }
      return false;
    });
  }

  onFileSelect(event: any) {
    const file = event.files[0];
    if (file?.size > this.maxSize) {
      this.uiService.showMessage(this.commonService.getLabel('file_upload_error'), AppSettings.MESSAGE_TYPE.ERROR);
      this.fileUpload?.clear();
      return;
    }

    if (file && this.accept && !this.isFileTypeAccepted(file)) {
      this.uiService.showMessage(this.commonService.getLabel('label_onlyjpg_jpeg_png_image_formats_are_supported'), AppSettings.MESSAGE_TYPE.ERROR);
      this.fileUpload?.clear();
      return;
    }

    if (this.isUploadLimitReached()) {
      return;
    }

    if (file) {
      this.setUploadedFileMeta(file);
      if (this.imageMode && file.type?.startsWith('image/')) {
        this.onDroppedFile.emit(file);
      }
      if (this.documentMode) {
        this.onDroppedFile.emit(file);
      }
    }
  }

  private isUploadLimitReached(): boolean {
    if (this.maxFileCount > 0 && this.currentFileCount >= this.maxFileCount) {
      this.uiService.showMessage(
        this.translate.instant('error_document_upload_max_limit', { max: this.maxFileCount }),
        AppSettings.MESSAGE_TYPE.ERROR
      );
      this.fileUpload?.clear();
      return true;
    }
    return false;
  }

  onUpload(event: any) {
    this.onFileUploadEvent.emit(event);
    const response = event.originalEvent?.body;
    if (response?.originalFileName && !this.uploadedFileName) {
      this.uploadedFileName = response.originalFileName;
    }
    this.onFileUpload.emit(response);
    this.loaderService.hide();
  }

  onBeforeUpload(event: any) {
    this.loaderService.show('page-center');
  }

  onUploadError(event: any) {
    this.uiService.showMessage(event['error']['error'].general[0].message, AppSettings.MESSAGE_TYPE.ERROR);
    this.loaderService.hide();
  }
}
